// Admins can override a featured product's Mindbody name from the staff portal
// (store.topItems[n].displayName). Purchases still key off the item id — this
// only changes what the client reads.
export function itemDisplayName(item) {
  return item?.displayName || item?.name || "";
}

/**
 * The tax Mindbody will charge on a line, in Mindbody's own arithmetic.
 *
 * Mindbody refuses a cart whose tendered total is not its total to the cent —
 * "The input payment total (32.5) does not match the calculated total (32.48)
 * for the entire cart" — so this cannot be approximately right.
 *
 * Two things about it are measured against carts Mindbody has actually billed,
 * not assumed:
 *
 *   • the tax is worked out on the whole line, then rounded once. Rounding a
 *     single unit's tax and multiplying by the quantity is what made 10 × $3
 *     snacks at 8.25% come to 32.50 when Mindbody wanted 32.48.
 *   • an exact half goes to the even cent, which is .NET's default and no
 *     surprise on Mindbody's platform: $10 at 8.25% is billed 0.82, $6 is
 *     billed 0.50, $18 is billed 1.48, $50 is billed 4.12.
 *
 * The halves only land on a half in decimal, so the multiplication is done in
 * whole cents; in floating point 6 × 0.0825 is 0.49499999999999994 and the
 * rule would never fire.
 */
const RATE_SCALE = 1000000;

export function lineTaxValue(unitPrice, taxRate, quantity = 1) {
  const price = Number(unitPrice);
  const rate = Number(taxRate);
  const qty = Number(quantity) || 1;
  if (!Number.isFinite(price) || !Number.isFinite(rate) || rate <= 0) return 0;

  const subtotalCents = Math.round(price * 100) * qty;
  const scaled = subtotalCents * Math.round(rate * RATE_SCALE);
  const whole = Math.floor(scaled / RATE_SCALE);
  const remainder = scaled - whole * RATE_SCALE;
  const half = RATE_SCALE / 2;
  const cents =
    remainder > half
      ? whole + 1
      : remainder < half
        ? whole
        : whole % 2 === 0
          ? whole
          : whole + 1;
  return cents / 100;
}

/**
 * What to tender for a store item, given the shape the kiosk keeps it in.
 *
 * `price` and `taxValue` here are already the whole line: the checkout modal
 * folds the quantity into both when it opens.
 *
 * The old form of this was `+item.price + (item.taxValue || 0)`, and that `|| 0`
 * is the bug this exists to make impossible to repeat. `GET /store/service/list`
 * — the event-ticket list — returned no tax field at all, so every event ticket
 * was tendered at its bare price, Mindbody rejected the cart, and the API's
 * retry quietly paid the right amount on a second call. Roughly one cart
 * purchase in eight, every day, for two and a half years.
 *
 * The API now prices the cart from Mindbody's catalogue before sending it, so
 * this number is a suggestion rather than the last word. It still has to be
 * right, because it is also the number on the screen in front of the client.
 */
export function itemCheckoutAmount(item) {
  const price = Number(item?.price) || 0;
  const tax = Number(item?.taxValue) || 0;
  return Math.round((price + tax) * 100) / 100;
}
